import { useState, type FormEvent, type ReactNode } from 'react';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { AsyncView, Button, Card, Checkbox, Dialog, Input, Select, Table, Textarea, useToast, type Column } from '../ui';
import { Drawer } from '../ui/Drawer';
import { Kebab, type AcaoKebab } from '../ui/Kebab';
import { api, ApiError } from '../lib/api';
import { podeAtuar, useSessao, type Role } from '../lib/session';
import { useResource } from '../lib/useResource';

/**
 * Tela de recurso, dirigida por configuração.
 *
 * Dez das dezesseis telas fazem a mesma coisa: listam um recurso, abrem um
 * diálogo para criar e apagam com confirmação. No front anterior isso estava
 * escrito dez vezes, e as dez divergiram — três não tinham estado de
 * carregamento, cinco não tratavam erro, duas apagavam sem confirmar, e o
 * `role` exigido para a mesma operação era diferente entre elas.
 *
 * Aqui a divergência custa mais do que a consistência: para uma tela se
 * comportar diferente é preciso mudar este arquivo, o que é revisável. Telas
 * com regra própria de verdade (caixa de entrada, métricas) não passam por
 * aqui — forçar tudo num molde só é a outra forma de errar.
 */

export type TipoCampo = 'text' | 'email' | 'tel' | 'url' | 'number' | 'password' | 'textarea' | 'select' | 'checkbox' | 'datetime-local';

export interface CampoForm {
  nome: string;
  rotulo: string;
  tipo?: TipoCampo;
  obrigatorio?: boolean;
  dica?: string;
  placeholder?: string;
  opcoes?: ReadonlyArray<{ value: string; label: string }>;
  /** Valor inicial na CRIAÇÃO. Existe porque a cor da etiqueta é obrigatória
   *  no servidor e o formulário a oferecia como se fosse opcional: em branco,
   *  a criação falhava com "Nome ou cor inválidos" sem dizer qual dos dois. */
  padrao?: string;
}

export interface RecursoConfig<T> {
  /** Caminho da API, sem `/api`. */
  path: string;
  /** Nome do recurso no plural e minúsculo: entra em "Carregando contatos". */
  rotuloPlural: string;
  colunas: ReadonlyArray<Column<T>>;
  chave: (row: T) => string;
  /** Nome da linha para o rótulo do menu de ações. Sem isto, trinta menus
   *  chamados "Ações" viram trinta itens idênticos na navegação por leitor. */
  rotuloLinha?: (row: T) => string;
  vazio: { title: string; text: string };
  /** Papel mínimo para ver a tela. Ausente = qualquer um. */
  verComo?: Role;
  /** Teto da consulta no servidor, quando existe. Ver `useResource`. */
  teto?: number;
  criar?: {
    rotulo: string;
    titulo: string;
    campos: readonly CampoForm[];
    papel: Role;
    /** Converte o formulário no corpo da requisição. */
    corpo?: (valores: Record<string, string>) => unknown;
  };
  /**
   * Ações extras por linha, para operações que não são criar/editar/excluir.
   *
   * Existe porque a API tem endpoints de efeito pontual — o consentimento
   * promocional do contato é um `PATCH` numa sub-rota com um booleano, não um
   * formulário. Sem isto, a alternativa seria transformar cada tela dessas num
   * componente próprio e perder a consistência que o molde garante.
   */
  acoesExtras?: (row: T, ferramentas: {
    recarregar: () => void;
    avisar: (mensagem: string, tom?: 'neutral' | 'danger') => void;
  }) => AcaoKebab[];

  /**
   * Criação com formulário próprio.
   *
   * `criar` cobre o caso comum: campos independentes, um POST, pronto. Não
   * cobre o disparo de campanha, que precisa filtrar os modelos pelo número
   * escolhido e consultar o alcance do segmento antes de agendar. Forçar isso
   * no molde genérico transformaria `CampoForm` num pequeno motor de
   * formulários — que é como o front anterior chegou onde chegou.
   */
  criarPersonalizado?: {
    rotulo: string;
    papel: Role;
    render: (fechar: () => void, concluir: (mensagem: string) => void) => ReactNode;
  };

  /**
   * Ação da tela inteira, não de uma linha.
   *
   * Existe por causa dos Modelos: eles são espelho da Meta, não têm criação
   * nem edição, e a única operação possível — sincronizar — não pertence a
   * nenhuma linha. O estado vazio dizia "Sincronize para trazê-los para cá" e
   * a tela não tinha onde clicar. Prometer uma ação e não oferecê-la é pior do
   * que não mencioná-la.
   */
  acaoGlobal?: {
    rotulo: string;
    icone?: ReactNode;
    papel: Role;
    /** Executa e devolve a frase de sucesso — o resultado importa: "nenhum
     *  modelo novo" e "12 sincronizados" pedem avisos diferentes. */
    executar: () => Promise<string>;
  };

  /**
   * Edição.
   *
   * Deliberadamente OPCIONAL, e ausente na maioria dos recursos: a API só
   * expõe atualização para operadores, regras automáticas, agentes de IA e
   * memórias. Etiquetas, contatos, disparos, agendamentos e modelos não têm
   * endpoint de update — modelos por natureza (são espelho da Meta), os demais
   * por lacuna do backend. Renderizar um botão "Editar" que abre um formulário
   * e falha no envio seria pior do que não ter o botão.
   */
  editar?: {
    titulo: string;
    papel: Role;
    method?: 'PATCH' | 'PUT';
    campos: readonly CampoForm[];
    /** Valores atuais da linha, para preencher o formulário. */
    valores: (row: T) => Record<string, string | boolean>;
    /** Quando a edição só vale para algumas linhas. O reagendamento existe só
     *  para envio PENDENTE — o servidor responde 404 para os demais, e um
     *  "Editar" que sempre falha é pior do que a ausência dele. */
    disponivel?: (row: T) => boolean;
    /** Monta o corpo. Schemas que exigem o objeto inteiro (é o caso de
     *  `PATCH /agents/:id`) precisam remontar tudo aqui, não só o que mudou. */
    corpo: (valores: Record<string, string>, row: T) => unknown;
    path?: (row: T) => string;
  };
  excluir?: {
    papel: Role;
    /** Frase da confirmação. Precisa nomear o que será apagado. */
    aviso: (row: T) => string;
    path?: (row: T) => string;
  };
}

/**
 * Renderiza os campos de um formulário a partir da configuração.
 *
 * Compartilhado entre criar e editar de propósito: eram dois blocos iguais na
 * primeira versão, e dois blocos iguais divergem — foi assim que o front
 * anterior acabou com um formulário de criação que validava o telefone e um de
 * edição que não validava.
 */
function Campos({
  campos, valores, criando = false,
}: {
  campos: readonly CampoForm[];
  valores?: Record<string, string | boolean>;
  /** Na criação o `padrao` do campo entra como valor inicial; na edição, não —
   *  ali quem manda é o valor da linha. */
  criando?: boolean;
}) {
  return (
    <>
      {campos.map((campo) => {
        const atual = valores?.[campo.nome] ?? (criando ? campo.padrao : undefined);
        if (campo.tipo === 'checkbox') {
          return (
            <Checkbox key={campo.nome} name={campo.nome} defaultChecked={atual === true}>
              {campo.rotulo}
            </Checkbox>
          );
        }
        if (campo.tipo === 'textarea') {
          return (
            <Textarea
              key={campo.nome}
              name={campo.nome}
              label={campo.rotulo}
              hint={campo.dica}
              required={campo.obrigatorio}
              defaultValue={typeof atual === 'string' ? atual : undefined}
            />
          );
        }
        if (campo.tipo === 'select') {
          return (
            <Select
              key={campo.nome}
              name={campo.nome}
              label={campo.rotulo}
              hint={campo.dica}
              required={campo.obrigatorio}
              options={campo.opcoes ?? []}
              placeholder={atual === undefined ? 'Selecione' : undefined}
              defaultValue={typeof atual === 'string' ? atual : undefined}
            />
          );
        }
        return (
          <Input
            key={campo.nome}
            name={campo.nome}
            label={campo.rotulo}
            hint={campo.dica}
            type={campo.tipo ?? 'text'}
            placeholder={campo.placeholder}
            required={campo.obrigatorio}
            defaultValue={typeof atual === 'string' ? atual : undefined}
          />
        );
      })}
    </>
  );
}

export function CrudScreen<T>({ config }: { config: RecursoConfig<T> }) {
  const { agent } = useSessao();
  const toast = useToast();
  const podeVer = !config.verComo || podeAtuar(agent.role, config.verComo);

  const { state, recarregar } = useResource<T[]>(config.path, {
    enabled: podeVer,
    teto: config.teto,
    disabledReason: `Esta seção é visível apenas para ${config.verComo === 'ADMIN' ? 'administradores' : 'gestores'}.`,
  });

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<T | null>(null);
  const [excluindo, setExcluindo] = useState<T | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erroForm, setErroForm] = useState<string | null>(null);

  const [executandoGlobal, setExecutandoGlobal] = useState(false);
  const [criandoProprio, setCriandoProprio] = useState(false);
  const podeCriarProprio = config.criarPersonalizado && podeAtuar(agent.role, config.criarPersonalizado.papel);
  const botaoProprio = podeCriarProprio ? (
    <Button variant="primary" icon={<Plus size={18} aria-hidden="true" />} onClick={() => setCriandoProprio(true)}>
      {config.criarPersonalizado!.rotulo}
    </Button>
  ) : null;
  const podeCriar = config.criar && podeAtuar(agent.role, config.criar.papel);
  const podeAcaoGlobal = config.acaoGlobal && podeAtuar(agent.role, config.acaoGlobal.papel);

  async function executarGlobal() {
    if (!config.acaoGlobal) return;
    setExecutandoGlobal(true);
    try {
      toast(await config.acaoGlobal.executar());
      recarregar();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Falha de conexão.', 'danger');
    } finally {
      setExecutandoGlobal(false);
    }
  }

  const botaoGlobal = podeAcaoGlobal ? (
    <Button
      variant={config.criar ? 'secondary' : 'primary'}
      icon={config.acaoGlobal!.icone}
      loading={executandoGlobal}
      loadingLabel={config.acaoGlobal!.rotulo}
      onClick={() => void executarGlobal()}
    >
      {config.acaoGlobal!.rotulo}
    </Button>
  ) : null;
  const podeEditar = config.editar && podeAtuar(agent.role, config.editar.papel);
  const podeExcluir = config.excluir && podeAtuar(agent.role, config.excluir.papel);

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!config.criar) return;
    const dados = Object.fromEntries(new FormData(event.currentTarget)) as Record<string, string>;
    setEnviando(true);
    setErroForm(null);
    try {
      await api(config.path, { method: 'POST', body: config.criar.corpo?.(dados) ?? dados });
      setCriando(false);
      recarregar();
      toast(`${config.criar.titulo} concluído.`);
    } catch (error) {
      // O erro fica NO diálogo, junto do formulário. Um toast some em 6s e
      // leva junto a única pista do que corrigir.
      setErroForm(error instanceof ApiError ? error.message : 'Falha de conexão.');
    } finally {
      setEnviando(false);
    }
  }

  async function salvarEdicao(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!config.editar || !editando) return;
    const form = new FormData(event.currentTarget);
    // Checkbox não enviado significa `false`; `Object.fromEntries` simplesmente
    // omite a chave, e um schema que exige booleano recusa o corpo inteiro.
    const dados: Record<string, string> = {};
    for (const campo of config.editar.campos) {
      dados[campo.nome] = campo.tipo === 'checkbox'
        ? String(form.get(campo.nome) === 'on')
        : String(form.get(campo.nome) ?? '');
    }
    setEnviando(true);
    setErroForm(null);
    try {
      const alvo = config.editar.path?.(editando) ?? `${config.path}/${config.chave(editando)}`;
      await api(alvo, { method: config.editar.method ?? 'PATCH', body: config.editar.corpo(dados, editando) });
      setEditando(null);
      recarregar();
      toast('Alterações salvas.');
    } catch (error) {
      setErroForm(error instanceof ApiError ? error.message : 'Falha de conexão.');
    } finally {
      setEnviando(false);
    }
  }

  async function confirmarExclusao() {
    if (!excluindo || !config.excluir) return;
    const alvo = config.excluir.path?.(excluindo) ?? `${config.path}/${config.chave(excluindo)}`;
    setEnviando(true);
    try {
      await api(alvo, { method: 'DELETE' });
      setExcluindo(null);
      recarregar();
      toast('Item excluído.');
    } catch (error) {
      setExcluindo(null);
      toast(error instanceof ApiError ? error.message : 'Falha ao excluir.', 'danger');
    } finally {
      setEnviando(false);
    }
  }

  const colunas: ReadonlyArray<Column<T>> = config.acoesExtras || podeEditar || podeExcluir
    ? [
        ...config.colunas,
        {
          key: '__acoes',
          header: 'Ações',
          align: 'end',
          cell: (row) => {
            const acoes: AcaoKebab[] = [
              ...(config.acoesExtras?.(row, { recarregar, avisar: toast }) ?? []),
              ...(podeEditar && (config.editar!.disponivel?.(row) ?? true) ? [{
                rotulo: 'Editar',
                icone: <Pencil size={16} aria-hidden="true" />,
                onSelect: () => { setErroForm(null); setEditando(row); },
              }] : []),
              ...(podeExcluir ? [{
                rotulo: 'Excluir',
                icone: <Trash2 size={16} aria-hidden="true" />,
                perigo: true,
                separar: true,
                onSelect: () => setExcluindo(row),
              }] : []),
            ];
            return <Kebab rotulo={config.rotuloLinha?.(row) ?? 'este item'} acoes={acoes} />;
          },
        },
      ]
    : config.colunas;

  return (
    <>
      <Card
        actions={
          botaoGlobal || botaoProprio || podeCriar ? (
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {botaoGlobal}
              {botaoProprio}
              {podeCriar && (
                <Button
                  variant="primary"
                  icon={<Plus size={18} aria-hidden="true" />}
                  onClick={() => { setErroForm(null); setCriando(true); }}
                >
                  {config.criar!.rotulo}
                </Button>
              )}
            </div>
          ) : undefined
        }
      >
        <AsyncView
          state={state}
          label={config.rotuloPlural}
          empty={{
            title: config.vazio.title,
            text: config.vazio.text,
            action: podeCriar ? (
              <Button variant="secondary" icon={<Plus size={18} aria-hidden="true" />} onClick={() => setCriando(true)}>
                {config.criar!.rotulo}
              </Button>
            ) : botaoProprio ?? botaoGlobal ?? undefined,
          }}
        >
          {(linhas) => (
            <Table
              caption={config.rotuloPlural}
              columns={colunas}
              rows={linhas}
              rowKey={config.chave}
            />
          )}
        </AsyncView>
      </Card>

      {/* Montado só quando aberto: o formulário próprio carrega os dados
          auxiliares dele (modelos, números, etiquetas) ao montar. */}
      {criandoProprio && config.criarPersonalizado?.render(
        () => setCriandoProprio(false),
        (mensagem) => { setCriandoProprio(false); recarregar(); toast(mensagem); },
      )}

      {config.criar && (
        <Drawer
          open={criando}
          onClose={() => setCriando(false)}
          title={config.criar.titulo}
          footer={
            <>
              <Button variant="secondary" onClick={() => setCriando(false)}>Cancelar</Button>
              <Button variant="primary" type="submit" form="form-crud" loading={enviando} icon={<Check size={18} aria-hidden="true" />}>
                Salvar
              </Button>
            </>
          }
        >
          <form id="form-crud" onSubmit={enviar} style={{ display: 'grid', gap: 'var(--space-4)' }}>
            {erroForm && (
              <p className="field__error" role="alert">{erroForm}</p>
            )}
            <Campos campos={config.criar.campos} criando />
          </form>
        </Drawer>
      )}

      {config.editar && (
        <Drawer
          open={editando !== null}
          onClose={() => setEditando(null)}
          title={config.editar.titulo}
          footer={
            <>
              <Button variant="secondary" onClick={() => setEditando(null)}>Cancelar</Button>
              <Button variant="primary" type="submit" form="form-editar" loading={enviando} icon={<Check size={18} aria-hidden="true" />}>
                Salvar alterações
              </Button>
            </>
          }
        >
          {/* `key` no formulário força a remontagem ao trocar de linha. Sem
              isso o React reaproveita os inputs e os `defaultValue` da linha
              anterior permanecem — você abriria "editar Ana" com os dados do
              João. */}
          <form
            key={editando ? config.chave(editando) : 'nenhum'}
            id="form-editar"
            onSubmit={salvarEdicao}
            style={{ display: 'grid', gap: 'var(--space-4)' }}
          >
            {erroForm && <p className="field__error" role="alert">{erroForm}</p>}
            {editando && <Campos campos={config.editar.campos} valores={config.editar.valores(editando)} />}
          </form>
        </Drawer>
      )}

      {config.excluir && (
        <Dialog
          open={excluindo !== null}
          onClose={() => setExcluindo(null)}
          title="Confirmar exclusão"
          footer={
            <>
              <Button variant="secondary" onClick={() => setExcluindo(null)}>Cancelar</Button>
              <Button variant="danger" onClick={confirmarExclusao} loading={enviando} icon={<Trash2 size={18} aria-hidden="true" />}>
                Excluir definitivamente
              </Button>
            </>
          }
        >
          {/* A confirmação NOMEIA o alvo. "Tem certeza?" não dá informação
              nenhuma para decidir, e é o texto que estava nas duas telas que
              chegaram a confirmar alguma coisa. */}
          <p>{excluindo ? config.excluir.aviso(excluindo) : ''}</p>
        </Dialog>
      )}
    </>
  );
}
